import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ScrollToTopButton from '../components/ScrollToTopButton';

// Add global styles to prevent horizontal overflow
const GlobalWrapper = styled.div`
  overflow-x: hidden;
  width: 100%;
  position: relative;
`;

// Styled Components
const HeroSection = styled.section`
  height: 50vh;
  min-height: 300px;
  background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('/images/background_security.jpg') no-repeat center center/cover;
  display: flex;
  align-items: center;
  text-align: center;
  color: white;
  width: 100%;
`;

const Container = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
  box-sizing: border-box;
`;

const HeroContent = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
  box-sizing: border-box;
  
  h1 {
    font-size: 3rem;
    margin-bottom: 20px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    margin-bottom: 30px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
  }
`;

const Button = styled.a`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    padding: 10px 20px;
  }
`;

const ServicesContainer = styled.section`
  padding: 60px 0;
  background-color: white;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 40px;
  width: 100%;
  
  h2 {
    font-size: 2.5rem;
    color: var(--primary-color);
    position: relative;
    display: inline-block;
    padding-bottom: 15px;
    word-wrap: break-word;
    
    @media (max-width: 768px) {
      font-size: 2rem;
    }
    
    &::after {
      content: '';
      position: absolute;
      left: 50%;
      bottom: 0;
      transform: translateX(-50%);
      width: 80px;
      height: 4px;
      background-color: var(--accent-color);
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-bottom: 50px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const ServiceCard = styled.div`
  background-color: var(--light-color);
  padding: 30px;
  border-radius: 10px;
  box-shadow: var(--shadow);
  transition: transform 0.3s;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  box-sizing: border-box;
  
  &:hover {
    transform: translateY(-10px);
  }
  
  h3 {
    color: var(--primary-color);
    margin-bottom: 15px;
    font-size: 1.5rem;
    word-wrap: break-word;
  }
  
  p {
    margin-bottom: 20px;
    word-wrap: break-word;
  }
  
  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const BookingSection = styled.section`
  padding: 80px 0;
  background-color: var(--light-color);
  scroll-margin-top: 80px;
  width: 100%;
  
  @media (max-width: 768px) {
    padding: 40px 0;
  }
`;

const BookingForm = styled.form`
  max-width: 800px;
  margin: 0 auto;
  padding: 30px;
  background-color: white;
  border-radius: 10px;
  box-shadow: var(--shadow);
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 20px;
    width: 100%;
  }
`;

const FormRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: 0 -15px;
  width: 100%;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    margin: 0;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
  padding: 0 15px;
  width: ${props => props.half ? '50%' : '100%'};
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    width: 100%;
    padding: 0;
  }
  
  label {
    display: block;
    margin-bottom: 5px;
    font-weight: 500;
    word-wrap: break-word;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 12px;
    border: 1px solid #ddd;
    border-radius: 5px;
    font-size: 16px;
    box-sizing: border-box;
    
    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
  
  textarea {
    height: 150px;
    resize: vertical;
  }
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  
  input {
    width: auto;
    margin-right: 10px;
    margin-top: 4px;
  }
  
  label {
    flex: 1;
    word-wrap: break-word;
  }
`;

const SubmitButton = styled.button`
  display: inline-block;
  background-color: var(--accent-color);
  color: var(--dark-color);
  padding: 12px 30px;
  border: none;
  border-radius: 5px;
  font-weight: bold;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.3s;
  
  &:hover {
    background-color: #e0a800;
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    width: 100%;
  }
`;

const FormInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 10px;
  margin-bottom: 30px;
  border-left: 4px solid var(--accent-color);
  box-sizing: border-box;
  width: 100%;
  
  p {
    margin: 0;
    word-wrap: break-word;
  }
  
  @media (max-width: 768px) {
    padding: 15px;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d4edda;
  color: #155724;
  padding: 20px;
  border-radius: 10px;
  margin: 0 15px 20px;
  text-align: center;
  box-shadow: var(--shadow);
  box-sizing: border-box;
  
  h3 {
    margin-bottom: 15px;
    color: #155724;
    word-wrap: break-word;
  }
  
  p {
    margin-bottom: 20px;
    word-wrap: break-word;
  }
  
  button {
    margin-top: 15px;
    background-color: var(--accent-color);
    color: var(--dark-color);
    padding: 12px 30px;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s;
    
    &:hover {
      background-color: #e0a800;
      transform: translateY(-3px);
    }
    
    @media (max-width: 768px) {
      width: 80%;
    }
  }
`;

function Sicherheit() {
  const [selectedService, setSelectedService] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  
  // Check if URL has success parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      setShowSuccess(true);
      // Scroll to booking form
      document.getElementById('buchung').scrollIntoView({behavior: 'smooth'});
    }
  }, []);
  
  // Handle service card clicks
  const handleServiceCardClick = (service) => {
    setSelectedService(service);
    
    // Scroll to booking form
    document.getElementById('buchung').scrollIntoView({behavior: 'smooth'});
  };
  
  const resetForm = () => {
    setShowSuccess(false);
    setSelectedService('');
  };
  
  // Define security services
  const securityServices = [
    { id: 'objektschutz', name: 'Objektschutz/Werkschutz', description: 'Umfassender Schutz für Ihre Immobilien und Betriebsgelände. Unser professionelles Sicherheitspersonal sorgt für die Sicherheit Ihres Eigentums und gewährleistet den reibungslosen Betrieb durch Zugangskontrollen und regelmäßige Kontrollgänge.' },
    { id: 'empfangsdienst', name: 'Empfangsdienst/Pfortendienst', description: 'Repräsentative Empfangs- und Pfortendienste für Ihr Unternehmen. Unsere geschulten Mitarbeiter übernehmen die Besucherregistrierung, Telefonvermittlung und Zutrittskontrolle und sorgen so für einen professionellen ersten Eindruck.' },
    { id: 'revierdienst', name: 'Revierdienst', description: 'Regelmäßige Kontrollgänge und Überwachung mehrerer Objekte in einem festgelegten Revier. Unser mobiler Sicherheitsdienst bietet Ihnen flexible Schutzmaßnahmen und schnelle Reaktionszeiten bei sicherheitsrelevanten Vorfällen.' },
    { id: 'event-security', name: 'Security für Events', description: 'Maßgeschneiderte Sicherheitskonzepte für Veranstaltungen jeder Größe. Von der Einlasskontrolle über Besucherführung bis hin zur Bühnen- und VIP-Sicherheit sorgen wir für einen reibungslosen und sicheren Ablauf Ihrer Events.' },
    { id: 'brandwache', name: 'Brandwache', description: 'Fachkundige Brandschutzüberwachung bei Veranstaltungen, Schweißarbeiten oder in brandgefährdeten Bereichen. Unsere speziell ausgebildeten Brandschutzhelfer erkennen Gefahren frühzeitig und können im Ernstfall schnell und kompetent reagieren.' },
    { id: 'baustellenschutz', name: 'Baustellenschutz', description: 'Effektiver Schutz vor Diebstahl, Vandalismus und unbefugtem Betreten auf Baustellen. Mit regelmäßigen Kontrollgängen, Videoüberwachung und schneller Intervention bei Alarmen sichern wir Ihre wertvollen Materialien und Maschinen.' },
    { id: 'ladendetektiv', name: 'Ladendetektiv', description: 'Diskrete Überwachung und Prävention von Ladendiebstählen in Einzelhandelsgeschäften. Unsere erfahrenen Detektive arbeiten unauffällig, um Diebstähle zu verhindern und die Verluste Ihres Unternehmens zu minimieren.' },
    { id: 'personenschutz', name: 'Personenschutz', description: 'Professioneller Schutz für gefährdete Personen durch geschulte Sicherheitsexperten. Ob für Prominente, Führungskräfte oder bei besonderem Schutzbedarf -- wir garantieren höchste Diskretion und maximale Sicherheit in jeder Situation.' },
    { id: 'sonderschutz', name: 'Sonderschutz', description: 'Spezielle Sicherheitslösungen für besondere Anforderungen wie Werttransporte, Begleitschutz oder temporäre Bewachung von Hochrisikobereichen. Individuell angepasste Konzepte für Ihre spezifischen Sicherheitsbedürfnisse.' },
    { id: 'wachschutz', name: 'Wachschutz', description: 'Professionelle Bewachung und dauerhafte Präsenz an schutzbedürftigen Orten. Unser ausgebildetes Sicherheitspersonal übernimmt die kontinuierliche Überwachung Ihrer Objekte und gewährleistet eine schnelle Reaktion bei Sicherheitsvorfällen.' },
    { id: 'alarmverfolgung', name: 'Alarmverfolgung', description: 'Schnelle Reaktion und Überprüfung bei Auslösung Ihrer Alarmanlagen. Unser Interventionsdienst ist rund um die Uhr einsatzbereit, um ausgelöste Alarme zu verifizieren und bei Bedarf notwendige Maßnahmen einzuleiten.' },
    { id: 'kaufhausdetektiv', name: 'Kaufhausdetektiv', description: 'Spezialisierte Überwachung in Kaufhäusern und größeren Einzelhandelsflächen. Unsere Detektive sind auf die Erkennung von Diebstahlmustern spezialisiert und helfen, organisierte Ladendiebstähle aufzudecken und zu verhindern.' }
  ];
  
  return (
    <GlobalWrapper>
      <HeroSection className="hero">
        <Container>
          <HeroContent className="hero-content">
            <h1>Sicherheitsdienstleistungen</h1>
            <p>Professionelle Sicherheitslösungen für Ihre Immobilien, Veranstaltungen und Personen - zuverlässig, diskret und effektiv.</p>
            <Button href="#buchung" className="btn">Jetzt anfragen</Button>
          </HeroContent>
        </Container>
      </HeroSection>

      <ServicesContainer className="services-container">
        <Container>
          <SectionTitle className="section-title">
            <h2>Unsere Sicherheitsdienstleistungen</h2>
          </SectionTitle>
          
          <ServicesGrid className="services-grid">
            {securityServices.map(service => (
              <ServiceCard 
                key={service.id}
                id={service.id}
                className="service-card"
                data-service={service.name}
                onClick={() => handleServiceCardClick(service.name)}
                role="button"
                tabIndex="0"
                aria-label={`Service auswählen: ${service.name}`}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleServiceCardClick(service.name);
                  }
                }}
              >
                <h3>{service.name}</h3>
                <p>{service.description}</p>
              </ServiceCard>
            ))}
          </ServicesGrid>
        </Container>
      </ServicesContainer>

      <BookingSection id="buchung" className="booking-section">
        <Container>
          <SectionTitle className="section-title">
            <h2>Anfrage für Sicherheitsdienstleistungen</h2>
          </SectionTitle>
          
          {showSuccess ? (
            <SuccessMessage>
              <h3>Vielen Dank für Ihre Anfrage!</h3>
              <p>Wir haben Ihre Anfrage für Sicherheitsdienstleistungen erhalten und werden sie sorgfältig prüfen. Unser Team wird sich so schnell wie möglich mit Ihnen in Verbindung setzen, um die Details zu besprechen und ein individuelles Angebot zu erstellen.</p>
              <button onClick={resetForm}>Neue Anfrage</button>
            </SuccessMessage>
          ) : (
            <BookingForm className="booking-form" id="booking-form" action="https://formsubmit.co/info@guardiansmoves.de" method="POST">
              <FormInfo>
                <p><strong>Hinweis:</strong> Bitte füllen Sie das Formular vollständig aus. Wir werden Ihre Anfrage prüfen und uns umgehend bei Ihnen melden, um ein individuelles Angebot zu erstellen, das exakt auf Ihren Sicherheitsbedarf zugeschnitten ist.</p>
              </FormInfo>
              
              <input type="hidden" name="_subject" value="Neue Sicherheitsdienstleistungsanfrage über die Website" />
              <input type="hidden" name="_captcha" value="false" />
              <input type="hidden" name="_template" value="table" />
              <input type="hidden" name="_next" value={window.location.origin + window.location.pathname + "?success=true"} />
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="name">Name *</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="Nachname" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="vorname">Vorname *</label>
                  <input 
                    type="text" 
                    id="vorname" 
                    name="Name" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="phone">Telefonnummer</label>
                  <input 
                    type="tel" 
                    id="phone" 
                    name="Telefon" 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="email">E-Mail *</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="E-Mail" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="service">Gewünschter Service *</label>
                  <select 
                    id="service" 
                    name="Service" 
                    value={selectedService}
                    onChange={(e) => setSelectedService(e.target.value)}
                    required
                  >
                    <option value="">Bitte wählen</option>
                    {securityServices.map(service => (
                      <option key={service.id} value={service.name}>
                        {service.name}
                      </option>
                    ))}
                  </select>
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="company">Firma/Organisation</label>
                  <input 
                    type="text" 
                    id="company" 
                    name="Firma" 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup half className="form-group">
                  <label htmlFor="location">Einsatzort *</label>
                  <input 
                    type="text" 
                    id="location" 
                    name="Einsatzort" 
                    required 
                  />
                </FormGroup>
                <FormGroup half className="form-group">
                  <label htmlFor="start_date">Startdatum *</label>
                  <input 
                    type="date" 
                    id="start_date" 
                    name="Startdatum" 
                    required 
                  />
                </FormGroup>
              </FormRow>
              
              <FormRow className="form-row">
                <FormGroup className="form-group">
                  <label htmlFor="duration">Dauer/Zeitraum</label>
                  <input 
                    type="text" 
                    id="duration" 
                    name="Dauer" 
                    placeholder="z.B. 1 Woche, 3 Monate, fortlaufend" 
                  />
                </FormGroup>
              </FormRow>
              
              <FormGroup className="form-group">
                <label htmlFor="requirements">Anforderungen und Details *</label>
                <textarea 
                  id="requirements" 
                  name="Anforderungen" 
                  required
                  placeholder="Bitte beschreiben Sie Ihre Anforderungen und spezifischen Details (Anzahl der benötigten Sicherheitskräfte, besondere Qualifikationen, etc.)"
                ></textarea>
              </FormGroup>
              
              <FormGroup className="form-group">
                <CheckboxGroup>
                  <input 
                    type="checkbox" 
                    id="privacy" 
                    name="Datenschutz_akzeptiert" 
                    required 
                  />
                  <label htmlFor="privacy">
                    Ich habe die <a href="/datenschutz/" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a> gelesen und akzeptiere diese. *
                  </label>
                </CheckboxGroup>
              </FormGroup>
              
              <SubmitButton type="submit" className="btn">Anfrage senden</SubmitButton>
            </BookingForm>
          )}
        </Container>
      </BookingSection>
      
      <ScrollToTopButton />
    </GlobalWrapper>
  );
}

export default Sicherheit;